/**
 * Reasoning Component - Claude Desktop Style
 *
 * A bordered collapsible box that shows AI thinking process.
 * Similar to Claude's "Investigated unfamiliar term to deliver accurate information"
 *
 * Features:
 * - Clean bordered container
 * - Collapsible with descriptive summary
 * - Auto-expand during streaming
 * - Tool calls with visual indicators
 */

import * as React from 'react'
import { ChevronDown, Loader2, Wrench, CheckCircle2 } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'

interface ReasoningStep {
  type: string
  step?: string
  tools?: string[]
  count?: number
  elapsed?: string
}

interface ReasoningProps {
  steps: ReasoningStep[]
  duration?: string
  isStreaming?: boolean
  tokenUsage?: {
    promptTokens: number
    completionTokens: number
    totalTokens: number
  }
}

// Human-readable descriptions for different reasoning patterns
function getSummaryText(steps: ReasoningStep[]): string {
  const toolCalls = steps.filter(s => s.step === 'tool-call')
  const tools = toolCalls.flatMap(t => t.tools || [])

  // Determine what kind of work was done
  const hasPatientSearch = tools.some(t => t.toLowerCase().includes('patient'))
  const hasInvoiceSearch = tools.some(t => t.toLowerCase().includes('invoice'))
  const hasRevenueAnalysis = tools.some(t => t.toLowerCase().includes('revenue'))
  const hasTreatmentQuery = tools.some(t => t.toLowerCase().includes('treatment'))
  const hasChart = tools.some(t => t.toLowerCase().includes('chart'))
  const hasTable = tools.some(t => t.toLowerCase().includes('table'))
  const hasKnowledge = tools.some(t => t.toLowerCase().includes('knowledge') || t.toLowerCase().includes('search'))

  // Build descriptive summary
  const actions: string[] = []
  if (hasKnowledge) actions.push('searched knowledge base')
  if (hasPatientSearch) actions.push('queried patient data')
  if (hasInvoiceSearch) actions.push('analyzed invoices')
  if (hasRevenueAnalysis) actions.push('calculated revenue')
  if (hasTreatmentQuery) actions.push('looked up treatments')
  if (hasChart) actions.push('generated chart')
  if (hasTable) actions.push('created table')

  if (actions.length === 0) {
    return 'Analyzed your request'
  }

  const first = actions[0]!
  if (actions.length === 1) {
    return first.charAt(0).toUpperCase() + first.slice(1)
  } else if (actions.length === 2) {
    return `${first.charAt(0).toUpperCase() + first.slice(1)} and ${actions[1]}`
  } else {
    const last = actions.pop()
    return `${first.charAt(0).toUpperCase() + first.slice(1)}, ${actions.slice(1).join(', ')}, and ${last}`
  }
}

export function Reasoning({ steps, duration, isStreaming }: ReasoningProps) {
  const [isOpen, setIsOpen] = React.useState(isStreaming)

  // Auto-close when streaming ends
  React.useEffect(() => {
    if (!isStreaming && isOpen) {
      const timer = setTimeout(() => setIsOpen(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [isStreaming, isOpen])

  // Auto-open when streaming starts
  React.useEffect(() => {
    if (isStreaming) {
      setIsOpen(true)
    }
  }, [isStreaming])

  if (steps.length === 0 && !isStreaming) {
    return null
  }

  const summaryText = isStreaming ? 'Working on it...' : getSummaryText(steps)

  return (
    <div className="rounded-xl border border-border bg-card/50">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="flex w-full items-center gap-2 px-4 py-3 text-sm hover:bg-muted/30 transition-colors rounded-xl">
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform flex-shrink-0',
              !isOpen && '-rotate-90'
            )}
          />
          <span className="flex-1 text-left text-muted-foreground">
            {summaryText}
          </span>
          {isStreaming ? (
            <Loader2 className="h-4 w-4 animate-spin text-primary flex-shrink-0" />
          ) : duration ? (
            <span className="text-xs text-muted-foreground/70">{duration}s</span>
          ) : null}
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-3 pt-1 space-y-2 border-t border-border/50">
            {steps.map((step, i) => (
              <ReasoningStepItem key={i} step={step} />
            ))}
            {isStreaming && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>Processing...</span>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

function ReasoningStepItem({ step }: { step: ReasoningStep }) {
  // Tool call step
  if (step.step === 'tool-call' && step.tools) {
    return (
      <div className="flex items-start gap-2 text-sm">
        <Wrench className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
        <div className="flex-1 flex flex-wrap items-center gap-1">
          <span className="text-muted-foreground">Calling</span>
          {step.tools.map((tool, i) => (
            <React.Fragment key={tool}>
              {i > 0 && <span className="text-muted-foreground">,</span>}
              <code className="px-1.5 py-0.5 rounded-md bg-muted text-xs font-mono">
                {formatToolName(tool)}
              </code>
            </React.Fragment>
          ))}
          {step.elapsed && (
            <span className="text-xs text-muted-foreground/70 ml-1">({step.elapsed}s)</span>
          )}
        </div>
      </div>
    )
  }

  // Tool result step
  if (step.step === 'tool-result') {
    return (
      <div className="flex items-start gap-2 text-sm">
        <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-emerald-500 flex-shrink-0" />
        <div className="flex-1 text-muted-foreground">
          <span>Got </span>
          <span className="text-foreground font-medium">{step.count}</span>
          <span> result{step.count !== 1 ? 's' : ''}</span>
          {step.elapsed && (
            <span className="text-xs text-muted-foreground/70 ml-1">({step.elapsed}s)</span>
          )}
        </div>
      </div>
    )
  }

  return null
}

// Format tool name for display
function formatToolName(name: string): string {
  // Convert camelCase to readable format
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim()
}
